/**
 * Os dados das ações como a pessoa os escolhe, e a expressão que o serviço lê.
 *
 * A pessoa escreve texto normal e escolhe nas listas; daqui sai o JSONata:
 * o texto entre aspas, a lista de opções do menu, o prazo contado a partir de
 * agora. O caminho de volta lê o que estas funções gravaram, para a mesma
 * automação abrir do jeito que foi montada. O que não sai daqui (uma expressão
 * escrita à mão) não é lido: a tela mostra o texto como está.
 */

// ── As opções do menu ────────────────────────────────────────────────────

export interface OpcaoDoMenu {
  readonly id: string;
  readonly rotulo: string;
}

/** Até 10 opções; o rótulo cabe em 24 caracteres (20 quando vira botão, até 3). */
export const MAXIMO_DE_OPCOES = 10;
export const ROTULO_DA_LISTA = 24;
export const ROTULO_DO_BOTAO = 20;

/** O id de uma opção, tirado do rótulo: "Falar com atendente" vira `falar_com_atendente`. */
export function idDaOpcao(rotulo: string, usados: readonly string[]): string {
  const base =
    rotulo
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'opcao';
  let id = base;
  for (let n = 2; usados.includes(id); n++) id = `${base}_${n}`;
  return id;
}

/** As opções guardadas numa expressão de lista (`[{"id": …, "label": …}]`), ou `null`. */
export function opcoesDoMenu(expressao: string): OpcaoDoMenu[] | null {
  const texto = expressao.trim();
  if (!texto) return [];
  try {
    const lido: unknown = JSON.parse(texto);
    if (!Array.isArray(lido)) return null;
    const opcoes: OpcaoDoMenu[] = [];
    for (const item of lido) {
      if (typeof item !== 'object' || item === null) return null;
      const { id, label } = item as { id?: unknown; label?: unknown };
      if (typeof id !== 'string' || typeof label !== 'string') return null;
      opcoes.push({ id, rotulo: label });
    }
    return opcoes;
  } catch {
    return null;
  }
}

/** A expressão das opções; a opção sem rótulo não entra (ainda não é opção). */
export function expressaoDoMenu(opcoes: readonly OpcaoDoMenu[]): string {
  const cheias = opcoes.filter((o) => o.rotulo.trim() !== '');
  if (cheias.length === 0) return '';
  return JSON.stringify(cheias.map((o) => ({ id: o.id, label: o.rotulo.trim() })));
}

/** O campo do evento "O cliente escolheu uma opção do menu" que diz qual opção foi (#1012). */
export const CAMINHO_DA_OPCAO_ESCOLHIDA = 'reply.option_id';

/** O que basta de um bloco para achar os menus: a ação, os parâmetros e o corpo do "para cada". */
interface BlocoComMenu {
  readonly tipo: string;
  readonly acao?: string;
  /** Na ação, o objeto dos parâmetros; na consulta, uma lista. */
  readonly parametros?: unknown;
  readonly corpo?: { readonly nos: readonly BlocoComMenu[] };
}

/**
 * As opções dos menus que a própria automação manda, sem repetir o id: os
 * valores para escolher em "Opção escolhida". O menu mandado por outra
 * automação não está aqui; aí a pessoa escreve o id.
 */
export function opcoesDosMenus(
  nos: readonly BlocoComMenu[],
): { readonly valor: string; readonly rotulo: string }[] {
  const vistas = new Map<string, string>();
  const percorrer = (lista: readonly BlocoComMenu[]): void => {
    for (const no of lista) {
      if (no.tipo === 'acao' && no.acao === 'enviar_menu') {
        const opcoes = (no.parametros as Record<string, unknown> | undefined)?.['opcoes'];
        for (const o of opcoesDoMenu(typeof opcoes === 'string' ? opcoes : '') ?? []) {
          if (!vistas.has(o.id)) vistas.set(o.id, o.rotulo);
        }
      }
      if (no.corpo) percorrer(no.corpo.nos);
    }
  };
  percorrer(nos);
  return [...vistas].map(([valor, rotulo]) => ({ valor, rotulo }));
}

// ── As listas de texto (etiquetas, valores do modelo) ────────────────────

export function textosDaLista(expressao: string): string[] | null {
  const texto = expressao.trim();
  if (!texto) return [];
  try {
    const lido: unknown = JSON.parse(texto);
    return Array.isArray(lido) && lido.every((x) => typeof x === 'string') ? lido : null;
  } catch {
    return null;
  }
}

export function expressaoDaLista(textos: readonly string[]): string {
  const cheios = textos.map((t) => t.trim()).filter((t) => t !== '');
  return cheios.length > 0 ? JSON.stringify(cheios) : '';
}

// ── O prazo, a partir de agora ───────────────────────────────────────────

const HORA_MS = 3_600_000;

/** Os prazos que a tela oferece, em horas a partir de quando a automação roda. */
export const PRAZOS: readonly { horas: number; rotulo: string }[] = [
  { horas: 1, rotulo: 'Em 1 hora' },
  { horas: 4, rotulo: 'Em 4 horas' },
  { horas: 24, rotulo: 'Em 1 dia' },
  { horas: 48, rotulo: 'Em 2 dias' },
  { horas: 72, rotulo: 'Em 3 dias' },
  { horas: 168, rotulo: 'Em 7 dias' },
];

/** O prazo como expressão: o instante de agora mais as horas, no formato ISO. */
export function expressaoDoPrazo(horas: number): string {
  return `$fromMillis($toMillis($now()) + ${horas * HORA_MS})`;
}

export function horasDoPrazo(expressao: string): number | null {
  const m = /^\$fromMillis\(\$toMillis\(\$now\(\)\) \+ (\d+)\)$/.exec(expressao.trim());
  if (!m) return null;
  const ms = Number(m[1]);
  return ms % HORA_MS === 0 ? ms / HORA_MS : null;
}

// ── Onde pendurar a tarefa ───────────────────────────────────────────────

/** A tarefa pendurada na oportunidade do evento: `{"type": "oportunidade", "id": …}`. */
export function expressaoDoAlvo(caminhoDaOportunidade: string): string {
  return `{"type": "oportunidade", "id": ${caminhoDaOportunidade}}`;
}

export function caminhoDoAlvo(expressao: string): string | null {
  const m = /^\{"type": "oportunidade", "id": ([A-Za-z_][\w.]*)\}$/.exec(expressao.trim());
  return m ? m[1]! : null;
}

// ── O contato: o da conversa do evento ─────────────────────────────────────

/**
 * "O contato da conversa": a referência `conversa:<id>`, que o CRM troca pelo
 * cliente vinculado à conversa. O evento não traz telefone nem documento, e é
 * assim que a automação diz quem é sem expressão.
 */
export function expressaoDoContato(caminhoDaConversa: string): string {
  return `'conversa:' & ${caminhoDaConversa}`;
}

export function caminhoDoContato(expressao: string): string | null {
  const m = /^'conversa:'\s*&\s*([A-Za-z_][\w.]*)$/.exec(expressao.trim());
  return m ? m[1]! : null;
}

/** "A conversa do evento" → "da conversa do evento". */
export function deQuem(titulo: string): string {
  const [primeira, ...resto] = titulo.split(' ');
  const contracao: Record<string, string> = { A: 'da', O: 'do', As: 'das', Os: 'dos' };
  const de = contracao[primeira ?? ''];
  return de ? [de, ...resto].join(' ') : `de ${titulo}`;
}

// ── Os campos do card e do contato ───────────────────────────────────────

/** Os campos de texto fixo, como objeto: `{"nome": "Lembrete"}`. Vazios não entram. */
export function expressaoDosCampos(campos: Readonly<Record<string, string>>): string {
  const cheios = Object.entries(campos)
    .map(([k, v]) => [k, v.trim()] as const)
    .filter(([, v]) => v !== '');
  return cheios.length > 0 ? JSON.stringify(Object.fromEntries(cheios)) : '';
}

export function camposDaExpressao(expressao: string): Record<string, string> | null {
  const texto = expressao.trim();
  if (!texto) return {};
  try {
    const lido: unknown = JSON.parse(texto);
    if (typeof lido !== 'object' || lido === null || Array.isArray(lido)) return null;
    const campos: Record<string, string> = {};
    for (const [k, v] of Object.entries(lido)) {
      if (typeof v !== 'string') return null;
      campos[k] = v;
    }
    return campos;
  } catch {
    return null;
  }
}
